import { useCallback, useEffect, useRef, useState } from 'react';
import { useHypoteseOvergang, type Overgang } from '../api/useHypoteseBehandling.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { ANGRE_SEKUNDER } from '../innboks/Toast.tsx';

export { ANGRE_SEKUNDER };

/**
 * En overgang som sender et svar til innsenderen, holdt tilbake i klienten til angre-vinduet er
 * ute (DD-70; «Hypotesekontrakten før H1.4», 2026-09-26). Overgangene er append-only og det
 * finnes ingen «angre»-endepunkter: «Angre» betyr at kallet ALDRI ble sendt, og tjeneren ser
 * bare det som ble sendt. Å gå fra siden — eller lukke fanen — sender straks.
 */
export interface Ventende {
  id: string;
  nummer?: number;
  overgang: Overgang;
  /** Toastens tekst mens den venter: hva som skjer, og at svaret sendes når tiden er ute. */
  tekst: string;
}

export function useAngre({ onSendt, onFeil }: { onSendt: (v: Ventende, h: Hypothesis) => void; onFeil: (v: Ventende, e: unknown) => void }) {
  const overgang = useHypoteseOvergang();
  const [vent, setVent] = useState<{ v: Ventende; sekunder: number } | null>(null);
  const holdt = useRef<{ timer: ReturnType<typeof setTimeout>; teller: ReturnType<typeof setInterval>; v: Ventende } | null>(null);
  const sendt = useRef(onSendt);
  sendt.current = onSendt;
  const feilet = useRef(onFeil);
  feilet.current = onFeil;
  const mutate = useRef(overgang.mutateAsync);
  mutate.current = overgang.mutateAsync;

  const send = useCallback((v: Ventende) => {
    mutate
      .current({ id: v.id, overgang: v.overgang })
      .then((h) => sendt.current(v, h))
      .catch((e: unknown) => feilet.current(v, e));
  }, []);

  /** Sender det som venter, nå. Uten noe som venter, gjør den ingenting. */
  const sendNaa = useCallback(
    (oppdater = true) => {
      const h = holdt.current;
      if (!h) return;
      clearTimeout(h.timer);
      clearInterval(h.teller);
      holdt.current = null;
      if (oppdater) setVent(null);
      send(h.v);
    },
    [send],
  );

  const start = useCallback(
    (v: Ventende) => {
      // Én ting venter om gangen: det som alt venter, sendes før det neste holdes tilbake.
      sendNaa();
      let sekunder = ANGRE_SEKUNDER;
      setVent({ v, sekunder });
      const teller = setInterval(() => {
        sekunder -= 1;
        if (sekunder > 0) setVent({ v, sekunder });
      }, 1000);
      const timer = setTimeout(() => {
        clearInterval(teller);
        holdt.current = null;
        setVent(null);
        send(v);
      }, ANGRE_SEKUNDER * 1000);
      holdt.current = { timer, teller, v };
    },
    [send, sendNaa],
  );

  /** «Angre»: kallet sendes aldri. Returnerer det som ventet, så arket kan stå som det var. */
  const angre = useCallback((): Ventende | null => {
    const h = holdt.current;
    if (!h) return null;
    clearTimeout(h.timer);
    clearInterval(h.teller);
    holdt.current = null;
    setVent(null);
    return h.v;
  }, []);

  // Å gå fra siden sender straks (også når fanen lukkes): ingenting blir liggende usendt.
  useEffect(() => {
    const vedLukk = () => sendNaa(false);
    window.addEventListener('pagehide', vedLukk);
    return () => {
      window.removeEventListener('pagehide', vedLukk);
      sendNaa(false);
    };
  }, [sendNaa]);

  return { vent, start, angre, sendNaa, sender: overgang.isPending };
}
